import { useState } from "react";
import { Phone, Mail, ArrowUpRight, ShieldCheck, CheckCircle2 } from "lucide-react";
import Seo from "../components/Seo";
import PageHeader from "../components/PageHeader";
import BookingLink from "../components/BookingLink";
import { Reveal } from "../components/Reveal";
import { BUSINESS } from "../lib/site";

const SERVICES = [
  "Get a quote",
  "General question",
  "Business accounting & bookkeeping",
  "Business tax planning",
  "Personal tax planning",
  "Something else",
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service_interest: SERVICES[0], message: "", website: "" });
  const [state, setState] = useState("idle");
  const [errors, setErrors] = useState({});
  const [serverMsg, setServerMsg] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    setErrors({});
    setServerMsg("");
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setState("success");
      } else if (res.status === 400 && data.errors) {
        setErrors(data.errors);
        setState("idle");
      } else {
        setServerMsg(data.detail || "We couldn’t accept your message right now. Please call or email us instead.");
        setState("error");
      }
    } catch (err) {
      setServerMsg("We couldn’t reach the server. Please call or email us instead.");
      setState("error");
    }
  };

  const inputCls = (err) => `w-full rounded-md border ${err ? "border-red-700" : "border-olive/30"} bg-white px-4 py-3 text-base text-ink placeholder:text-moss/50 focus:border-olive focus:outline-none`;

  return (
    <>
      <Seo
        title="Contact & Inquiry Form | Directive Optive Solutions"
        description="Reach Directive Optive Solutions: call (801) 474-5890, email info@directiveoptivesolutions.com, book a consultation online, or send a general inquiry."
        path="/contact"
      />
      <PageHeader
        eyebrow="Contact"
        title="Let’s start the conversation."
        lead="Call, email, book online, or use the form below. Whatever is easiest. The first conversation is simply about where you are and what you need."
        image="/img/photo-handshake.webp"
        imageAlt="A client handshake over a desk with a notebook"
      />

      <section className="py-24 lg:py-32" data-testid="contact-section">
        <div className="container-shell grid gap-16 lg:grid-cols-[1.1fr_0.9fr]">
          <Reveal>
            {state === "success" ? (
              <div className="card-sage flex flex-col items-start gap-5 p-10" data-testid="contact-success-panel">
                <CheckCircle2 className="h-10 w-10 text-pine" />
                <h2 className="font-serif text-3xl text-olive">Thank you. Your message was received.</h2>
                <p className="text-base leading-relaxed text-moss">We’ve saved your inquiry and will reply to <span className="font-bold text-ink">{form.email}</span> as soon as we can. Usually within one to two business days. If it’s urgent, the fastest paths are a call or booking directly:</p>
                <div className="mt-2 flex flex-col gap-4 sm:flex-row">
                  <a href={BUSINESS.phoneHref} data-testid="success-phone-link" className="btn-outline"><Phone className="h-4 w-4" /> {BUSINESS.phone}</a>
                  <BookingLink page="contact-success" dataTestId="success-book-button" className="btn-primary">Book a Consultation <ArrowUpRight className="h-4 w-4" /></BookingLink>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="inquiry-form" noValidate>
                <h2 className="font-serif text-3xl text-olive">Send us a note</h2>
                <p className="mt-4 rounded-md border border-olive/25 bg-sage px-5 py-4 text-sm leading-relaxed text-moss">
                  <span className="font-bold text-ink">A friendly warning:</span> please do not include Social Security numbers, account numbers, or tax documents. This form is for general questions only.
                </p>
                <div className="mt-8 grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="cf-name" className="mb-2 block text-sm font-bold text-ink">Name <span className="text-red-700" aria-hidden>*</span></label>
                    <input id="cf-name" data-testid="contact-name-input" type="text" autoComplete="name" required value={form.name} onChange={set("name")} className={inputCls(errors.name)} placeholder="Your name" />
                    {errors.name && <p className="mt-2 text-sm text-red-700" data-testid="contact-name-error">{errors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="cf-email" className="mb-2 block text-sm font-bold text-ink">Email <span className="text-red-700" aria-hidden>*</span></label>
                    <input id="cf-email" data-testid="contact-email-input" type="email" autoComplete="email" required value={form.email} onChange={set("email")} className={inputCls(errors.email)} placeholder="you@example.com" />
                    {errors.email && <p className="mt-2 text-sm text-red-700" data-testid="contact-email-error">{errors.email}</p>}
                  </div>
                  <div>
                    <label htmlFor="cf-phone" className="mb-2 block text-sm font-bold text-ink">Phone <span className="font-normal text-moss">(optional)</span></label>
                    <input id="cf-phone" data-testid="contact-phone-input" type="tel" autoComplete="tel" value={form.phone} onChange={set("phone")} className={inputCls(errors.phone)} placeholder="(555) 555-5555" />
                    {errors.phone && <p className="mt-2 text-sm text-red-700" data-testid="contact-phone-error">{errors.phone}</p>}
                  </div>
                  <div>
                    <label htmlFor="cf-service" className="mb-2 block text-sm font-bold text-ink">Service interest</label>
                    <select id="cf-service" data-testid="contact-service-select" value={form.service_interest} onChange={set("service_interest")} className={inputCls()}>
                      {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="cf-message" className="mb-2 block text-sm font-bold text-ink">Message <span className="font-normal text-moss">(optional)</span></label>
                    <textarea id="cf-message" data-testid="contact-message-input" rows="5" maxLength="2000" value={form.message} onChange={set("message")} className={inputCls(errors.message)} placeholder="Tell us a little about what you need. No sensitive details, please." />
                    {errors.message && <p className="mt-2 text-sm text-red-700" data-testid="contact-message-error">{errors.message}</p>}
                  </div>
                </div>
                {/* Honeypot. Hidden from humans, catches bots */}
                <div className="absolute left-[-9999px] top-[-9999px]" aria-hidden="true">
                  <label htmlFor="cf-website">Website</label>
                  <input id="cf-website" type="text" tabIndex="-1" autoComplete="off" value={form.website} onChange={set("website")} />
                </div>
                {state === "error" && (
                  <div className="mt-6 rounded-md border border-red-700/40 bg-red-50 p-5" data-testid="contact-error-panel">
                    <p className="text-sm leading-relaxed text-red-900">{serverMsg}</p>
                    <p className="mt-2 text-sm text-red-900"><a className="font-bold underline" href={BUSINESS.phoneHref}>{BUSINESS.phone}</a> · <a className="font-bold underline" href={BUSINESS.emailHref}>{BUSINESS.email}</a></p>
                  </div>
                )}
                <button type="submit" disabled={state === "sending"} data-testid="contact-submit-button" className="btn-primary mt-8 disabled:cursor-not-allowed disabled:opacity-60">
                  {state === "sending" ? "Sending…" : "Send message"}
                </button>
                <p className="mt-4 flex items-center gap-2 text-xs text-moss"><ShieldCheck className="h-4 w-4 text-olive" /> Your inquiry is stored securely on our servers and used only to reply to you.</p>
              </form>
            )}
          </Reveal>

          <Reveal delay={0.12} className="flex flex-col gap-6">
            <div className="card-sage p-8" data-testid="contact-alternatives-panel">
              <p className="eyebrow">Other ways to reach us</p>
              <ul className="mt-6 space-y-5">
                <li>
                  <a href={BUSINESS.phoneHref} data-testid="contact-page-phone-link" className="group flex items-center gap-4">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-olive"><Phone className="h-5 w-5" /></span>
                    <span><span className="block text-xs font-semibold uppercase tracking-[0.14em] text-moss">Call</span><span className="text-lg font-bold text-olive group-hover:text-pine">{BUSINESS.phone}</span></span>
                  </a>
                </li>
                <li>
                  <a href={BUSINESS.emailHref} data-testid="contact-page-email-link" className="group flex items-center gap-4">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-olive"><Mail className="h-5 w-5" /></span>
                    <span><span className="block text-xs font-semibold uppercase tracking-[0.14em] text-moss">Email</span><span className="break-all text-lg font-bold text-olive group-hover:text-pine">{BUSINESS.email}</span></span>
                  </a>
                </li>
                <li>
                  <BookingLink page="contact-page" dataTestId="contact-page-book-button" className="flex w-full items-center gap-4 text-left">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-pine text-white"><ArrowUpRight className="h-5 w-5" /></span>
                    <span><span className="block text-xs font-semibold uppercase tracking-[0.14em] text-moss">Book online</span><span className="text-lg font-bold text-olive group-hover:text-pine">Book a Consultation</span></span>
                  </BookingLink>
                </li>
              </ul>
            </div>
            <div className="rounded-md border border-olive/25 bg-sage p-8">
              <h3 className="font-serif text-xl text-olive">What happens next</h3>
              <p className="mt-3 text-sm leading-relaxed text-moss">We read every message and reply personally. No auto-quotes, no sales scripts. If we’re not the right fit for what you need, we’ll tell you and point you somewhere that is.</p>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
